import React, { useEffect, useRef, useState } from "react"
import "./App.css"

const wsHost = window.location.hostname == "localhost" ? "ws://localhost:8803" : "wss://" + window.location.host

function buildWsURL(): string {
  const storedSecret = localStorage.getItem("playerSecret")
  const baseURL = wsHost + `/currentGame/ws`
  if (storedSecret) {
    return `${baseURL}?secret=${storedSecret}`
  }
  return baseURL
}

interface PlayerInfo {
  name: string
  score: number
  color: string
}

interface FoundWord {
  startX: number
  startY: number
  endX: number
  endY: number
  color: string
}

interface FullUpdate {
  playerSecret: number
  playerName: string
  playerColor: string
  matrix: string[]
  players: PlayerInfo[]
  foundWords: FoundWord[]
}

interface ServerMessage {
  fullUpdate?: FullUpdate
  foundAWord?: FoundWord
  invalidSelection?: { message: string }
  playersUpdate?: PlayerInfo[]
}

interface Cell {
  x: number
  y: number
}

function App() {
  const socket = useRef<WebSocket | null>(null)
  const errorTimeout = useRef<NodeJS.Timeout | null>(null)
  const matrixRef = useRef<HTMLDivElement | null>(null)
  const [socketStatus, setSocketStatus] = useState("uninitialized")
  const [fullUpdate, setFullUpdate] = useState<FullUpdate | null>(null)
  const [foundWords, setFoundWords] = useState<FoundWord[]>([])
  const [selectedStart, setSelectedStart] = useState<Cell | null>(null)
  const [selectedEnd, setSelectedEnd] = useState<Cell | null>(null)
  const [isSwiping, setIsSwiping] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>("")

  useEffect(() => {
    const ws = new WebSocket(buildWsURL())
    socket.current = ws

    ws.onopen = (e) => {
      setSocketStatus("open")
    }

    ws.onclose = (e) => {
      setSocketStatus("closed")
    }

    ws.onmessage = (e) => {
      const msg: ServerMessage = JSON.parse(e.data)

      if (msg.fullUpdate) {
        setFullUpdate(msg.fullUpdate)
        setFoundWords(msg.fullUpdate.foundWords)
        localStorage.setItem("playerSecret", msg.fullUpdate.playerSecret.toString())
      }

      if (msg.foundAWord) {
        setFoundWords((prev) => [...prev, msg.foundAWord!])
      }

      if (msg.playersUpdate) {
        setFullUpdate((prev) => {
          if (!prev) return prev
          return { ...prev, players: msg.playersUpdate! }
        })
      }

      if (msg.invalidSelection) {
        if (errorTimeout.current) {
          clearTimeout(errorTimeout.current)
        }
        setErrorMessage(msg.invalidSelection.message)
        errorTimeout.current = setTimeout(() => setErrorMessage(""), 2000)
      }
    }

    ws.onerror = (e) => {
      setSocketStatus("error")
      console.log("WS error", e)
    }

    return () => {
      ws.close()
    }
  }, [])

  const sendSelection = (startX: number, startY: number, endX: number, endY: number) => {
    if (!socket.current) return

    const msg = {
      selectWord: {
        startX,
        startY,
        endX,
        endY,
      },
    }

    socket.current.send(JSON.stringify(msg))
  }

  const getCellFromTouch = (touch: React.Touch): Cell | null => {
    if (!matrixRef.current) return null

    const cells = matrixRef.current.querySelectorAll(".cell")
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i] as HTMLElement
      const rect = cell.getBoundingClientRect()
      if (
        touch.clientX >= rect.left &&
        touch.clientX <= rect.right &&
        touch.clientY >= rect.top &&
        touch.clientY <= rect.bottom
      ) {
        const x = parseInt(cell.getAttribute("data-x") || "0")
        const y = parseInt(cell.getAttribute("data-y") || "0")
        return { x, y }
      }
    }
    return null
  }

  const handleCellClick = (x: number, y: number) => {
    if (isSwiping) return

    if (!selectedStart) {
      setSelectedStart({ x, y })
    } else {
      sendSelection(selectedStart.x, selectedStart.y, x, y)
      setSelectedStart(null)
    }
  }

  const handleTouchStart = (e: React.TouchEvent, x: number, y: number) => {
    e.preventDefault()
    setIsSwiping(true)
    setSelectedStart({ x, y })
    setSelectedEnd({ x, y })
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    if (!isSwiping || !selectedStart) return

    const touch = e.touches[0]
    const cell = getCellFromTouch(touch)
    if (cell) {
      setSelectedEnd(cell)
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault()
    if (!isSwiping || !selectedStart || !selectedEnd) {
      setIsSwiping(false)
      setSelectedStart(null)
      setSelectedEnd(null)
      return
    }

    sendSelection(selectedStart.x, selectedStart.y, selectedEnd.x, selectedEnd.y)
    setIsSwiping(false)
    setSelectedStart(null)
    setSelectedEnd(null)
  }

  const isCellFound = (x: number, y: number): string | null => {
    for (const word of foundWords) {
      const isHorizontal = word.startY === word.endY
      const isVertical = word.startX === word.endX

      if (isHorizontal && y === word.startY) {
        const minX = Math.min(word.startX, word.endX)
        const maxX = Math.max(word.startX, word.endX)
        if (x >= minX && x <= maxX) {
          return word.color
        }
      }

      if (isVertical && x === word.startX) {
        const minY = Math.min(word.startY, word.endY)
        const maxY = Math.max(word.startY, word.endY)
        if (y >= minY && y <= maxY) {
          return word.color
        }
      }
    }
    return null
  }

  const isCellSelected = (x: number, y: number): boolean => {
    if (!selectedStart) return false

    if (!selectedEnd || !isSwiping) {
      return selectedStart.x === x && selectedStart.y === y
    }

    const isHorizontal = selectedStart.y === selectedEnd.y
    const isVertical = selectedStart.x === selectedEnd.x

    if (isHorizontal && y === selectedStart.y) {
      const minX = Math.min(selectedStart.x, selectedEnd.x)
      const maxX = Math.max(selectedStart.x, selectedEnd.x)
      return x >= minX && x <= maxX
    }

    if (isVertical && x === selectedStart.x) {
      const minY = Math.min(selectedStart.y, selectedEnd.y)
      const maxY = Math.max(selectedStart.y, selectedEnd.y)
      return y >= minY && y <= maxY
    }

    return false
  }

  const matrix = fullUpdate && (
    <div className="matrix" ref={matrixRef} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {fullUpdate.matrix.map((row: string, y: number) => (
        <div className="row" key={y}>
          {[...row].map((letter, x) => {
            const foundColor = isCellFound(x, y)
            const isSelected = isCellSelected(x, y)
            const className = `cell ${foundColor ? "found" : ""} ${isSelected ? "selected" : ""}`

            return (
              <div
                key={x}
                className={className}
                style={foundColor ? { backgroundColor: foundColor } : {}}
                onClick={() => handleCellClick(x, y)}
                onTouchStart={(e) => handleTouchStart(e, x, y)}
                data-x={x}
                data-y={y}
              >
                {letter}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )

  const playersList = fullUpdate && (
    <div className="players">
      <h3>Players</h3>
      {fullUpdate.players.map((player, idx) => (
        <div key={idx} className="player" style={{ color: player.color }}>
          <span className="player-name">{player.name}</span>
          <span className="player-score">{player.score}</span>
        </div>
      ))}
    </div>
  )

  return (
    <div className="game">
      <div className="header">
        <div>
          {socketStatus != "open" && <pre>WS status: {socketStatus}</pre>}
          {fullUpdate && (
            <h3 style={{ color: fullUpdate.playerColor }}>Hello {fullUpdate.playerName}</h3>
          )}
          <div className="error-container">
            {errorMessage && <div className="error">{errorMessage}</div>}
          </div>
        </div>
        {playersList}
      </div>
      {matrix}
    </div>
  )
}

export default App
